'use client';

import React, { useState } from 'react';
import { cn } from '@/lib/cn';
import {
  Carousel,
  CarouselControls,
  FullScreenCarousel,
  type CarouselItem,
} from '@/components/carousel';
import { Image } from '@/components/ui/Image';
import { SectionHeader } from '@/components/ui/SectionHeader';
import type { BuildingImage } from '@repo/api';

interface BuildingPhotosProps {
  ownerId: string;
  images?: BuildingImage[] | null;
}

export function BuildingPhotos({ ownerId, images }: BuildingPhotosProps) {
  const photos = (images ?? [])
    .filter(img => typeof img?.url === 'string' && img.url.length > 0)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (photos.length === 0) {
    return null;
  }

  const carouselId = `contact-building-photos-${ownerId}`;
  const fullscreenCarouselId = `${carouselId}-fullscreen`;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const openFullscreen = (index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => setIsFullscreen(false);

  const items: CarouselItem[] = photos.map((photo, index) => ({
    id: `${carouselId}-${index}`,
    content: (
      <button
        type="button"
        onClick={() => openFullscreen(index)}
        className={cn('w-full text-left cursor-pointer', 'bg-primary rounded-2xl overflow-hidden')}
        aria-label={`Open building photo ${index + 1} fullscreen`}
      >
        <Image
          aspectRatio="4:3"
          quality={75}
          src={photo.url}
          className="w-full"
          alt={photo.description || `Building photo ${index + 1}`}
          sizes="300px"
        />
      </button>
    ),
  }));

  return (
    <div className="flex flex-col gap-2 items-start w-full">
      <div className="flex items-center justify-between w-full pr-4">
        <SectionHeader title="Building photos" />
        {photos.length > 2 && (
          <CarouselControls
            carouselId={carouselId}
            className="hidden md:flex gap-2"
            prevClassName="!h-8 !w-8"
            nextClassName="!h-8 !w-8"
          />
        )}
      </div>
      {/* <div className="-mx-6 md:mx-6"> */}
      <Carousel
        items={items}
        carouselId={carouselId}
        perView={{ mobile: 1, tablet: 2, desktop: 2 }}
        peek={{ mobile: 24, tablet: 0, desktop: 0 }}
        padStart={{ mobile: 24, tablet: 12, desktop: 12 }}
        padEnd={{ mobile: 24, tablet: 12, desktop: 12 }}
        gap={16}
        className="-mx-4 md:mx-0 w-screen md:w-full"
        ariaLabel="Building photos"
      />
      {/* </div> */}

      <FullScreenCarousel
        open={isFullscreen}
        carouselId={fullscreenCarouselId}
        title="Building photos"
        initialIndex={fullscreenIndex}
        onClose={closeFullscreen}
        items={photos.map((photo, index) => ({
          id: `${fullscreenCarouselId}-${index}`,
          src: photo.url,
          alt: photo.description || `Building photo ${index + 1}`,
        }))}
      />
    </div>
  );
}
